import Button from '@orn/shared-web/components/Button/Button';
import Input from '@orn/shared-web/components/Input';
import Select from '@orn/shared-web/components/Select/Select';
import { CarIcon, TrashIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useDeleteVehicle } from '../../../hooks/mutations/useDeleteVehicle';
import { useUpsertVehicle } from '../../../hooks/mutations/useUpsertVehicle';
import useAutoCommit from '../../../hooks/useAutoCommit';
import { VEHICLE_OWNERSHIP_OPTIONS } from '../../../lib/dependents/constants';
import { CollapsibleSettingsCard } from '../common';

import { buildVehicleTitle, buildVehicleUpsertInput, vehicleEditsUnchanged } from './possessionsCardModel';

import type { VehicleOwnership } from '../../../__generated__/globalTypes';
import type { IVehicle } from '../../../hooks/queries/useVehicles';

export interface IVehicleCardBaseProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  make: string;
  model: string;
  ownership: VehicleOwnership | null;
  onMakeChange: (value: string) => void;
  onModelChange: (value: string) => void;
  onOwnershipChange: (value: VehicleOwnership) => void;
  onCommit: () => void;
  onDelete: () => void;
  /** Locks every field + the trash — while deleting, or while a draft's create is in flight. */
  isDisabled: boolean;
}

/**
 * Presentational vehicle card: a collapsible card with a car icon + year/make/
 * model title, a trash button in the header (left of the caret, shown while
 * expanded), and editable Make / Model / Ownership fields. Fully controlled —
 * state + callbacks come from {@link VehicleCard} (existing rows) or the draft
 * card (new rows).
 */
export const VehicleCardBase = ({
  title,
  isExpanded,
  onToggle,
  make,
  model,
  ownership,
  onMakeChange,
  onModelChange,
  onOwnershipChange,
  onCommit,
  onDelete,
  isDisabled,
}: IVehicleCardBaseProps) => {
  const ownershipKey = VEHICLE_OWNERSHIP_OPTIONS.some((o) => o.id === ownership) ? (ownership ?? undefined) : undefined;

  return (
    <CollapsibleSettingsCard
      isExpanded={isExpanded}
      onToggle={onToggle}
      toggleLabel={title}
      header={
        <div className="flex items-center gap-2">
          <CarIcon className="text-quaternary size-5 shrink-0" aria-hidden />
          <span className="text-primary text-lg font-semibold">{title}</span>
        </div>
      }
      headerAction={
        isExpanded ? (
          <Button
            color="tertiary"
            size="sm"
            iconLeading={TrashIcon}
            aria-label="Delete vehicle"
            onClick={onDelete}
            isDisabled={isDisabled}
          />
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input
          label="Make"
          placeholder="e.g. Toyota"
          value={make}
          onChange={onMakeChange}
          onBlur={onCommit}
          size="md"
          isDisabled={isDisabled}
        />
        <Input
          label="Model"
          placeholder="e.g. Camry"
          value={model}
          onChange={onModelChange}
          onBlur={onCommit}
          size="md"
          isDisabled={isDisabled}
        />
        <Select
          label="Ownership"
          placeholder="Select ownership"
          size="md"
          items={VEHICLE_OWNERSHIP_OPTIONS}
          selectedKey={ownershipKey}
          onSelectionChange={(key) => onOwnershipChange(key as VehicleOwnership)}
          isDisabled={isDisabled}
        >
          {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      </div>
    </CollapsibleSettingsCard>
  );
};

VehicleCardBase.displayName = 'VehicleCardBase';

export interface IVehicleCardProps {
  vehicle: IVehicle;
}

/**
 * Data container for one existing vehicle. Owns the local Make / Model /
 * Ownership edit state and persists inline via `upsertVehicle` (make/model on
 * blur, ownership on change) — commits run through `useAutoCommit`, so the
 * fields stay enabled while a save is in flight and overlapping triggers
 * serialize. Deletes via `deleteVehicle`, which refetches `GetVehicles` so the
 * card drops out of the list.
 */
const VehicleCard = ({ vehicle }: IVehicleCardProps) => {
  const [upsertVehicle] = useUpsertVehicle();
  const [deleteVehicle, { loading: isDeleting }] = useDeleteVehicle();
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [make, setMake] = React.useState(vehicle.make ?? '');
  const [model, setModel] = React.useState(vehicle.model ?? '');
  const [ownership, setOwnership] = React.useState<VehicleOwnership>(vehicle.ownership);

  // The unchanged-guard diffs against the `vehicle` row, which Apollo updates
  // from each mutation response — so consecutive commits compare correctly.
  const commit = async () => {
    if (vehicleEditsUnchanged(vehicle, make, model, ownership)) {
      return;
    }
    try {
      await upsertVehicle({ variables: { input: buildVehicleUpsertInput(vehicle, make, model, ownership) } });
    } catch (error) {
      logger.error('Failed to save vehicle', error);
    }
  };

  const requestCommit = useAutoCommit(commit);

  const handleOwnershipChange = (value: VehicleOwnership) => {
    setOwnership(value);
    requestCommit();
  };

  const handleDelete = React.useCallback(() => {
    void deleteVehicle({ variables: { input: { vehicleId: vehicle.id } }, refetchQueries: ['GetVehicles'] });
  }, [deleteVehicle, vehicle.id]);

  return (
    <VehicleCardBase
      title={buildVehicleTitle(vehicle.year, make, model)}
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded((v) => !v)}
      make={make}
      model={model}
      ownership={ownership}
      onMakeChange={setMake}
      onModelChange={setModel}
      onOwnershipChange={handleOwnershipChange}
      onCommit={requestCommit}
      onDelete={handleDelete}
      isDisabled={isDeleting}
    />
  );
};

export default VehicleCard;
